import Axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore, useSelector } from 'react-redux';
import { FormController, useOneFormContext } from 'src/feature-app';
import { FormDataGroup, FormInfo, OneFormState, Steps, MolecularResponse } from 'src/types';
import { OneElementTemplateFullScreen } from 'src/feature-app/Screen';
import { Text } from '@volkswagen-onehub/components-core';
import { RatingStars, FechaCompra, SuggestModelo, PantallaIntroduccionStars } from 'src/components';
import { ConfirmacionNoCompra, ThankYouNoCompra } from '.';
import { formatHeaders, formDataBuilder } from '../format-fields';
import { dpsSend } from 'src/bff';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { NoCompraFormProps } from './NoCompraController';
import { useInitializeNoCompraForm } from './use-initialize-no-compra-form';
import { useTrackingManager } from 'src/feature-app/hooks/use-tracking-manager';
import { getVehiculosModelo } from 'src/bff';

export enum NewCarFormSteps {
  FechaCompra,
  Modelo,
  Valoracion,
  Plazos,
  Visita,
  Precio,
  Equipamiento,
  Consumo,
  Garantia,
  Financiacion,
  Confirmacion,
}

const getModeloInteresOutputs = (modelo: string, list: any) => {
  const aux = list.find((mod: any) => mod.name === 'modelo');
  return [aux.title];
};

export function NoCompraYaTengo({ dpsData, idLead, idPerson, handleError, trackFormLoad, estado }: NoCompraFormProps) {
  const { initializeForm, nextStep } = useOneFormContext();
  const trackingManager = useTrackingManager();
  const store = useStore();
  const dispatch = useDispatch();
  const { formData } = useSelector((state: OneFormState) => state);
  const [loadingPresubmit, setLoadingPresubmit] = useState(true);
  const { modelo } = formData;

  //out modelo
  const [list, setList] = useState(null);

  useEffect(() => {
    getVehiculosModelo().then((modelos) => {
      setList(modelos);
    });
  }, []);

  const updateModelo = (modelo: string) => {
    const outputs = getModeloInteresOutputs(modelo, list);

    let newSteps = [...steps];

    const modeloIndex = newSteps.findIndex((step: Steps) => step.name === 'Modelo');
    newSteps[modeloIndex].outputsText = outputs;

    dispatch({ type: 'UPDATE_STEPS', payload: newSteps });
  };

  useEffect(() => {
    const sendPresubmit = async () => {
      const result = await sendForm(true);

      if (!result) {
        handleError();
      }
      setLoadingPresubmit(false);
    };

    sendPresubmit();
  }, []);

  const steps: Steps[] = [
    {
      title: (
        <>
          Nos encantaría saber más. <Text bold>¿Desde cuándo tienes coche nuevo?</Text>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={
            <div style={{ marginBottom: '2px' }}>
              <FechaCompra notALayer={true} />
            </div>
          }
        />
      ),
      screenIndex: NewCarFormSteps.FechaCompra,
      name: 'FechaCompra',
      outputs: ['fecha'],
      outputsText: ['fecha'],
    },
    {
      title: (
        <>
          Por curiosidad,{' '}
          <Text bold>
            ¿qué modelo{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              es?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          allowOverflowingContent
          element={
            <div style={{ marginBottom: '2px' }}>
              <SuggestModelo notALayer={true} fieldsName="modelo" required />
            </div>
          }
        />
      ),
      screenIndex: NewCarFormSteps.Modelo,
      name: 'Modelo',
      outputs: ['modelo'],
      outputsText: ['modelo'],
      showOverflow: true,
    },
    {
      title: (
        <>
          Nos hubiera encantado que fuera un Volkswagen. <Text bold>¿Nos ayudas a mejorar?</Text>
        </>
      ),
      fields: <PantallaIntroduccionStars />,
      screenIndex: NewCarFormSteps.Valoracion,
      name: 'Valoracion',
    },
    {
      title: <Text bold>Los plazos de entrega</Text>,
      alternateTitle: <Text bold>Los plazos de entrega</Text>,
      fields: (
        <OneElementTemplateFullScreen
          element={
            <RatingStars name="plazos" nextOnClick={true} textGood="satisfactorios" textBad="insatisfactorios" />
          }
        />
      ),
      screenIndex: NewCarFormSteps.Plazos,
      name: 'Plazos',
      outputs: ['plazos'],
      outputsText: ['plazos'],
      outputsIsStars: true,
    },
    {
      title: <Text bold>Tu visita al punto de venta</Text>,
      fields: (
        <OneElementTemplateFullScreen
          element={<RatingStars name="visita" nextOnClick={true} textGood="satisfactoria" textBad="insatisfactoria" />}
        />
      ),
      screenIndex: NewCarFormSteps.Visita,
      name: 'Visita',
      outputs: ['visita'],
      outputsText: ['visita'],
      outputsIsStars: true,
    },
    {
      title: <Text bold>El precio del modelo</Text>,
      fields: (
        <OneElementTemplateFullScreen
          element={<RatingStars name="precio" nextOnClick={true} textGood="satisfactorio" textBad="insatisfactorio" />}
        />
      ),
      screenIndex: NewCarFormSteps.Precio,
      name: 'Precio',
      outputs: ['precio'],
      outputsText: ['precio'],
      outputsIsStars: true,
    },
    {
      title: <Text bold>El equipamiento y los asistentes adicionales</Text>,
      fields: (
        <OneElementTemplateFullScreen
          element={
            <RatingStars name="equipamiento" nextOnClick={true} textGood="satisfactorios" textBad="insatisfactorios" />
          }
        />
      ),
      screenIndex: NewCarFormSteps.Equipamiento,
      name: 'Equipamiento',
      outputs: ['equipamiento'],
      outputsText: ['equipamiento'],
      outputsIsStars: true,
    },
    {
      title: <Text bold>El consumo del modelo</Text>,
      fields: (
        <OneElementTemplateFullScreen
          element={<RatingStars name="consumo" nextOnClick={true} textGood="satisfactorio" textBad="insatisfactorio" />}
        />
      ),
      screenIndex: NewCarFormSteps.Consumo,
      name: 'Consumo',
      outputs: ['consumo'],
      outputsText: ['consumo'],
      outputsIsStars: true,
    },
    {
      title: <Text bold>La garantía del modelo</Text>,
      fields: (
        <OneElementTemplateFullScreen
          element={
            <RatingStars name="garantia" nextOnClick={true} textGood="satisfactoria" textBad="insatisfactoria" />
          }
        />
      ),
      screenIndex: NewCarFormSteps.Garantia,
      name: 'Garantia',
      outputs: ['garantia'],
      outputsText: ['garantia'],
      outputsIsStars: true,
    },
    {
      title: <Text bold>Las condiciones de financiación</Text>,
      fields: (
        <OneElementTemplateFullScreen
          element={
            <RatingStars name="financiacion" nextOnClick={true} textGood="satisfactorias" textBad="insatisfactorias" />
          }
        />
      ),
      screenIndex: NewCarFormSteps.Financiacion,
      name: 'Financiacion',
      outputs: ['financiacion'],
      outputsText: ['financiacion'],
      outputsIsStars: true,
    },
    {
      fields: <ConfirmacionNoCompra idPerson={idPerson} />,
      screenIndex: NewCarFormSteps.Confirmacion,
      name: 'Confirmacion',
      outputs: ['confirmacion'],
      outputsText: ['confirmacion'],
    },
  ];

  const NuevoFormData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW',
      DESC_VN_VO: 'VN',
      DESC_GDPR_ADAPTED: 'true',
    },
    fields: { formName: 'no_compra-ya_tengo', formType: 'otherform', pageCategory: 'Encuesta' }, // Analítica
  };

  const getFormInfo = (): {
    firstStep: Steps;
    lastStep: Steps;
    stepsHistory: number[];
    numberOfScreens: number;
    newSteps?: Steps[];
  } => {
    return {
      firstStep: steps[0],
      lastStep: steps[steps.length - 1],
      stepsHistory: [0],
      numberOfScreens: steps.length,
      newSteps: null,
    };
  };

  const generateData = (data?: Partial<FormDataGroup>) => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers } = formData;
    const { tc, today, url, device } = getParamsForm();

    return {
      'headers[TC]': tc,
      'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,
      'headers[FECHA_INSERTION_DATE]': today,
      'headers[DESC_VN_VO]': 'VN',

      'headers[DESC_TOKEN]': 'VW_DDB_FORM_NOCOMPRA', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_ID_CAMPAIGN]': '7011n000000Aj7uAAC',
      'headers[DESC_FORM_DETAIL]': 'NoCompra Grabación', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_FORMOBJECTIVE]': fields.quieroContacto === 'true' ? 'Más Información' : 'Encuesta',
      'headers[DESC_DEVICETYPE]': device,
      'headers[DESC_CAMPAIGNBLOCK]': '9. VW LEM',
      'headers[DESC_BRAND]': 'VW',
      'headers[COD_FORM_ID]': fields.formName ? `vw-oneform-${fields.formName}` : `vw-oneform-${data.fields.formName}`,
      'headers[DESC_URLORIGIN]': url,

      //'fields[NUM_CELLPHONE]': idPerson.NUM_Phone_mobile__c,
      //'fields[DESC_SURNAME_2]': idPerson.TXT_2_Surname__c,
      //'fields[DESC_SURNAME_1]': idPerson.LastName,
      //'fields[DESC_NAME]': idPerson.FirstName,
      'fields[DESC_LOPD]': 'PERMITE', // SOLO EN SUBMIT
      'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO', // SOLO EN SUBMIT
      'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
      'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
      'fields[DESC_FORM_VERSION]': fields.formName
        ? `vw-oneform-${fields.formName}-${process.env.VERSION}`
        : `vw-oneform-${data.fields.formName}-${process.env.VERSION}`,
      //'fields[DESC_EMAIL_ADDRESS]': idPerson.TXT_Email__c,
      //'fields[DESC_DEALER_SHARE]': fields.enviarInformacion === 'Sí' ? 'True' : 'False', // SOLO EN SUBMIT
      'fields[DESC_DEALER_CODE]': idLead.TXT_DESC_DEALER_CODE__c,
      'fields[DESC_DEALER]': idLead.DESC_Dealer_Code__c,
      'fields[DESC_AUXFIELD20]': idLead.External_Id_Climapro__c,
      'fields[DESC_AUXFIELD1]': 'Ya tengo',
      'fields[DESC_AUXFIELD2]': fields.fecha,
      'fields[DESC_AUXFIELD3]': fields.modelo,
      'fields[DESC_AUXFIELD7]': fields.plazos,
      'fields[DESC_AUXFIELD8]': fields.visita,
      'fields[DESC_AUXFIELD9]': fields.precio,
      'fields[DESC_AUXFIELD10]': fields.equipamiento,
      'fields[DESC_AUXFIELD11]': fields.consumo,
      'fields[DESC_AUXFIELD12]': fields.garantia,
      'fields[DESC_AUXFIELD13]': fields.financiacion,
      'fields[COD_LEAD_ID]': idLead.Id,
      'fields[DESC_DATA_ENRICHMENT]': 'NO INFORMADO',
      // ...getCarlineFields(fields, idLead),

      'flags[FLAG_ISLEAD]': 'false',
      'flags[FLAG_ISPROSPECT]': 'false',
      'flags[FLAG_GENERATELEM]': 'false',
    };
  };

  const generateSubmitData = () => generateData();

  /**
   * Generamos la misma data que para el submit y eliminamos todos los campos que no son necesarios para el presubmit.
   */
  const generatePresubmitData = () => {
    const data = generateData(NuevoFormData);

    //
    delete data['headers[DESC_VN_VO]'];
    delete data['fields[DESC_TRANF_DATA_THIRD]'];
    //

    delete data['headers[PINCODE]'];
    delete data['flags[FLAG_ISLEAD]'];
    delete data['flags[FLAG_ISPROSPECT]'];
    delete data['flags[FLAG_GENERATELEM]'];
    delete data['fields[DESC_AUXFIELD3]'];
    delete data['fields[DESC_AUXFIELD2]'];
    //delete data['fields[DESC_DEALER_SHARE]'];
    //delete data['fields[DESC_NAME]'];
    //delete data['fields[DESC_SURNAME_1]'];
    ////delete data['fields[DESC_SURNAME_2]'];
    //delete data['fields[DESC_EMAIL_ADDRESS]'];
    //delete data['fields[NUM_CELLPHONE]'];
    delete data['fields[DESC_LOPD]'];
    delete data['fields[DESC_LSSI]'];
    delete data['fields[DESC_LOPD_GDPR_PERFILADO]'];
    delete data['fields[DESC_DATA_ENRICHMENT]'];

    data['fields[DESC_AUXFIELD1]'] = 'Ya tengo';
    data['headers[DESC_FORM_DETAIL]'] = 'NoCompra Pregrabación';
    data['headers[DESC_TOKEN]'] = 'VW_DDB_FORM_NOCOMPRA_PRE';

    return data;
  };

  const sendForm = async (isPresubmit?: boolean): Promise<boolean> => {
    return new Promise<any>(async (resolve, reject) => {
      const data = isPresubmit ? generatePresubmitData() : generateSubmitData();
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response.data?.content?.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const { firstStep, lastStep, loading } = useInitializeNoCompraForm(steps);

  useEffect(() => {
    const NuevoFormInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens: steps.length,
      screenIndex: firstStep.screenIndex,
      formTheme: 'main',
      notALayer: true,
      formType: 'inPage',
      step: firstStep,
      steps,
      stepsHistory: [0],
      thankYouComponent: <ThankYouNoCompra />,
    };

    if (!loading) {
      initializeForm(NuevoFormInfo, NuevoFormData, true);
      trackFormLoad();
    }
  }, [loading]);

  useEffect(() => {
    if (modelo) updateModelo(modelo);
  }, [modelo]);

  return loading || loadingPresubmit || !steps ? null : <FormController steps={steps} screenType="full-screen" />;
}
